import { useEffect, useRef, useState } from "react";
import { Globe, ImageUp, Loader2, Megaphone, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSiteSettings, ogImageUrl } from "@/context/SiteSettingsContext";
import { MarqueeStrip } from "@/components/HomeMarquee";

const Counter = ({ value, max }) => (
  <span className={`text-[10px] ${value.length > max ? "text-rose-400" : "text-zinc-600"}`}>{value.length}/{max}</span>
);

const Field = ({ label, value, max, children }) => (
  <label className="mt-4 block">
    <span className="flex items-center justify-between text-xs uppercase tracking-widest text-zinc-500">{label}<Counter value={value} max={max} /></span>
    {children}
  </label>
);

const Previews = ({ title, desc, img }) => (
  <div className="mt-5 grid items-start gap-3 sm:grid-cols-2">
    <div className="rounded-xl bg-white p-4" data-testid="seo-preview-google">
      <div className="text-[11px] text-[#202124]">{window.location.host}</div>
      <div className="mt-0.5 line-clamp-1 text-[17px] text-[#1a0dab]">{title || "Judul SEO"}</div>
      <div className="mt-0.5 line-clamp-2 text-xs text-[#4d5156]">{desc || "Deskripsi SEO"}</div>
    </div>
    <div className="overflow-hidden rounded-xl bg-[#1f2c33]" data-testid="seo-preview-wa">
      {img ? <img src={img} alt="" className="aspect-[1.91/1] w-full object-cover" /> : <div className="grid aspect-[1.91/1] place-items-center bg-[#2a3942] text-[11px] text-zinc-400">Belum ada gambar</div>}
      <div className="p-3">
        <div className="line-clamp-1 text-sm font-semibold text-white">{title}</div>
        <div className="line-clamp-2 text-[11px] text-zinc-300">{desc}</div>
        <div className="mt-1 text-[10px] text-zinc-500">{window.location.host}</div>
      </div>
    </div>
  </div>
);

const OgImage = ({ canManage, version, reload }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      await reload();
      toast.success(ok);
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan gambar"));
    } finally {
      setBusy(false);
    }
  };
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Format harus PNG, JPG, atau WEBP");
    if (file.size > 1024 * 1024) return toast.error("Ukuran gambar maksimal 1MB");
    const reader = new FileReader();
    reader.onload = () => run(() => api.post("/admin/og-image", { image: reader.result }), "Gambar SEO berhasil diupload");
    reader.readAsDataURL(file);
  };
  if (!canManage) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="og-file-input" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="rk-btn-ghost inline-flex items-center gap-2 text-xs" data-testid="og-upload-btn">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <ImageUp size={13} />} {version ? "Ganti Gambar SEO" : "Upload Gambar SEO"} (1200×630, maks 1MB)
      </button>
      {version ? (
        <button type="button" onClick={() => run(() => api.delete("/admin/og-image"), "Gambar SEO dihapus")} disabled={busy} className="rk-btn-ghost inline-flex items-center gap-2 text-xs text-rose-400" data-testid="og-delete-btn">
          <Trash2 size={13} /> Hapus
        </button>
      ) : null}
    </div>
  );
};

export const SeoForm = ({ canManage }) => {
  const s = useSiteSettings();
  const [f, setF] = useState({ seo_title: s.seo_title, seo_description: s.seo_description, seo_keywords: s.seo_keywords });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setF({ seo_title: s.seo_title, seo_description: s.seo_description, seo_keywords: s.seo_keywords });
  }, [s.seo_title, s.seo_description, s.seo_keywords]);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/seo", f);
      await s.reload();
      toast.success("Pengaturan SEO disimpan");
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan SEO"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="rk-glass rounded-2xl p-6 lg:col-span-2" data-testid="seo-settings-form">
      <h3 className="flex items-center gap-2 text-lg font-bold"><Globe size={18} className="text-[#e5b80b]" /> Pengaturan SEO</h3>
      <p className="mt-1 text-xs text-zinc-500">Judul & deskripsi langsung terbaca Google. Pratinjau link WhatsApp/Facebook ikut berubah setelah website dipublikasikan ulang.</p>
      <div className="grid gap-x-5 lg:grid-cols-2">
        <div>
          <Field label="Judul SEO" value={f.seo_title} max={70}>
            <input value={f.seo_title} onChange={set("seo_title")} disabled={!canManage} maxLength={70} className="rk-input mt-2" data-testid="seo-title-input" />
          </Field>
          <Field label="Meta Deskripsi" value={f.seo_description} max={160}>
            <textarea value={f.seo_description} onChange={set("seo_description")} disabled={!canManage} maxLength={160} rows={3} className="rk-input mt-2 resize-none" data-testid="seo-description-input" />
          </Field>
          <Field label="Kata Kunci (pisahkan koma)" value={f.seo_keywords} max={200}>
            <input value={f.seo_keywords} onChange={set("seo_keywords")} disabled={!canManage} maxLength={200} placeholder="top up royal dream, bongkar chip, ..." className="rk-input mt-2" data-testid="seo-keywords-input" />
          </Field>
          <OgImage canManage={canManage} version={s.og_image_version} reload={s.reload} />
        </div>
        <Previews title={f.seo_title} desc={f.seo_description} img={ogImageUrl(s.og_image_version)} />
      </div>
      {canManage ? (
        <button type="submit" disabled={busy || f.seo_title.trim().length < 3 || f.seo_description.trim().length < 10} className="rk-btn-primary mt-5 inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="seo-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan SEO
        </button>
      ) : null}
    </form>
  );
};

export const MarqueeForm = ({ canManage }) => {
  const s = useSiteSettings();
  const [f, setF] = useState({ marquee_enabled: s.marquee_enabled, marquee_text: s.marquee_text, marquee_speed: s.marquee_speed });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setF({ marquee_enabled: s.marquee_enabled, marquee_text: s.marquee_text, marquee_speed: s.marquee_speed });
  }, [s.marquee_enabled, s.marquee_text, s.marquee_speed]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/marquee", f);
      await s.reload();
      toast.success("Teks berjalan disimpan");
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan teks berjalan"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="rk-glass rounded-2xl p-6 lg:col-span-2" data-testid="marquee-settings-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-lg font-bold"><Megaphone size={18} className="text-[#e5b80b]" /> Teks Berjalan (Beranda)</h3>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={f.marquee_enabled} disabled={!canManage} onChange={(e) => setF((x) => ({ ...x, marquee_enabled: e.target.checked }))} className="h-4 w-4 accent-[#e5b80b]" data-testid="marquee-toggle" />
          {f.marquee_enabled ? "ON" : "OFF"}
        </label>
      </div>
      <Field label="Isi teks" value={f.marquee_text} max={300}>
        <input value={f.marquee_text} onChange={(e) => setF((x) => ({ ...x, marquee_text: e.target.value }))} disabled={!canManage} maxLength={300} placeholder="Contoh: Promo bonus chip setiap hari Jumat!" className="rk-input mt-2" data-testid="marquee-text-input" />
      </Field>
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="marquee-speed">
        <span className="text-xs uppercase tracking-widest text-zinc-500">Kecepatan</span>
        {[["slow", "Lambat"], ["normal", "Normal"], ["fast", "Cepat"]].map(([k, l]) => (
          <button key={k} type="button" disabled={!canManage} onClick={() => setF((x) => ({ ...x, marquee_speed: k }))} data-testid={`marquee-speed-${k}`}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${f.marquee_speed === k ? "bg-[#e5b80b] font-semibold text-black" : "border border-white/10 text-zinc-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {f.marquee_text.trim() ? <div className="mt-4 overflow-hidden rounded-lg"><MarqueeStrip text={f.marquee_text} speed={f.marquee_speed} testid="marquee-preview" /></div> : null}
      {canManage ? (
        <button type="submit" disabled={busy || (f.marquee_enabled && !f.marquee_text.trim())} className="rk-btn-primary mt-5 inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="marquee-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan Teks Berjalan
        </button>
      ) : null}
    </form>
  );
};
